import React from 'react';
import styled from 'styled-components';
import WorkIcon from '@mui/icons-material/Work';

const Document = styled.img`
    display: none;
    height: 70px;
    width: fit-content;
    background-color: #000;
    border-radius: 10px;
    &:hover{
        cursor: pointer;
        opacity: 0.8;
    }
`;

const Description = styled.div`
    width: 100%;
    font-size: 14px;
    font-weight: 400;
    color: ${({ theme }) => theme.text_primary + 'cc'};
    margin-bottom: 10px;
    @media only screen and (max-width: 768px){
        font-size: 12px;
    }
`;

const BulletList = styled.ul`
    padding-left: 18px;
    margin: 8px 0;
    display: flex;
    flex-direction: column;
    gap: 6px;
`;

const BulletItem = styled.li`
    line-height: 1.5;
    color: ${({ theme }) => theme.text_primary + 'dd'};
`;

const Span = styled.span`
    display: block;
    line-height: 1.5;
`;

const Card = styled.div`
    width: 650px;
    border-radius: 10px;
    background-color: ${({ theme }) => theme.colors.background2};
    box-shadow: rgba(23, 92, 230, 0.15) 0px 4px 24px;
    padding: 16px 20px;
    justify-content: space-between;
    position: relative;
    overflow: hidden;
    display: flex;
    flex-direction: column;
    gap: 12px;
    transition: all 0.3s ease-in-out;
    &:hover {
        box-shadow: 0px 0px 20px rgba(0,0,0,0.2);
        transform: translateY(-5px);
    }
    @media only screen and (max-width: 900px) {
        width: 95vw;
        min-width: 0;
        max-width: 500px;
        padding: 12px;
        gap: 8px;
    }
    @media only screen and (max-width: 600px) {
        width: 85vw;
        min-width: 0;
        max-width: 99vw;
        padding: 10px;
        gap: 6px;
    }

    &:hover ${Document}{
        display: flex;
    }

    border: 0.1px solid #306EE8;
`;

const Top = styled.div`
    width: 100%;
    display: flex;
    gap: 12px;
    align-items: center;
    @media only screen and (max-width: 500px){
        flex-direction: row;
        align-items: center;
        gap: 8px;
    }
`;

const Image = styled.img`
    height: 50px;
    width: 50px;
    object-fit: contain;
    background-color: #ffffff;
    border-radius: 10px;
    padding: 2px;
    box-shadow: 0 2px 8px rgba(0,0,0,0.15);
    @media only screen and (max-width: 768px){
        height: 40px;
        width: 40px;
    }
    @media only screen and (max-width: 500px){
        height: 36px;
        width: 36px;
    }
`;

const FallbackBadge = styled.div`
    height: 50px;
    width: 50px;
    min-width: 50px;
    border-radius: 10px;
    background: linear-gradient(135deg, ${({ theme }) => theme.primary}, #854CE6);
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
    font-weight: 700;
    font-size: 20px;
    box-shadow: 0 4px 10px rgba(0,0,0,0.2);
    @media only screen and (max-width: 768px){
        height: 40px;
        width: 40px;
        min-width: 40px;
        font-size: 16px;
    }
    @media only screen and (max-width: 500px){
        height: 36px;
        width: 36px;
        min-width: 36px;
        font-size: 14px;
    }
`;

const Body = styled.div`
    width: 100%;
    display: flex;
    flex-direction: column; 
`;

const Role = styled.div`
    font-size: 18px;
    font-weight: 600;
    color: ${({ theme }) => theme.text_primary};
    @media only screen and (max-width: 768px){
        font-size: 14px;
    }
`;

const Company = styled.div`
    font-size: 14px;
    font-weight: 500;
    color: ${({ theme }) => theme.text_secondary};
    @media only screen and (max-width: 768px){
        font-size: 12px;
    }
`;

const Date = styled.div`
    font-size: 12px;
    font-weight: 400;
    color: ${({ theme }) => theme.text_secondary + '99'};
    @media only screen and (max-width: 768px){
        font-size: 10px;
    }
`;

const Skills = styled.div`
    width: 100%;
    display: flex;
    gap: 8px;
    margin-top: 4px;
    flex-direction: column;
`;

const ItemWrapper = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
`;

const Skill = styled.div`
    font-size: 13px;
    font-weight: 500;
    color: ${({ theme }) => theme.primary};
    background: ${({ theme }) => theme.primary + '18'};
    padding: 2px 10px;
    border-radius: 8px;
    border: 1px solid ${({ theme }) => theme.primary + '33'};
    @media only screen and (max-width: 768px){
        font-size: 11px;
    }
`;

const ExperienceCard = ({ experience }) => {
    return (
        <Card>
            <Top>
                {experience.img ? (
                    <Image src={experience.img} alt={experience.company} />
                ) : (
                    <FallbackBadge>
                        <WorkIcon fontSize="small" />
                    </FallbackBadge>
                )}
                <Body>
                    <Role>{experience.role}</Role>
                    <Company>{experience.company}</Company>
                    <Date>{experience.date}</Date>
                </Body>
            </Top>
            <Description>
                {experience?.desc && (
                    <Span>{experience?.desc}</Span>
                )}
                {experience?.bullets && (
                    <BulletList>
                        {experience.bullets.map((bullet, idx) => (
                            <BulletItem key={idx}>{bullet}</BulletItem>
                        ))}
                    </BulletList>
                )}
                {experience?.skills && (
                    <Skills>
                        <b style={{ color: '#854CE6', fontSize: '13px' }}>Key Tech:</b>
                        <ItemWrapper>
                            {experience?.skills?.map((skill, index) => (
                                <Skill key={skill + index}>{skill}</Skill>
                            ))}
                        </ItemWrapper>
                    </Skills>
                )}
            </Description>
            {experience.doc && (
                <a href={experience.doc} target="_blank" rel="noopener noreferrer">
                    <Document src={experience.doc} alt="Verification Document" />
                </a>
            )}
        </Card>
    );
};

export default ExperienceCard;
